
import * as React from "react"

import { cn } from "../../lib/utils"

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, onFocus, value, ...props }, ref) => {
    // ─── Numeric input UX fixes ─────────────────────────────────────────
    // 1) Display 0 as an empty field (placeholder shows instead) so the
    //    user never sees a stuck "0" that they'd have to delete before
    //    typing a real value. Typing "1200000" into an empty field gives
    //    "1200000", not "01200000".
    // 2) Select existing content on focus so the user can immediately type
    //    over a non-zero value (e.g. replacing 50000 with 75000).
    // Consumer-supplied onFocus still runs after our auto-select.
    const isNumeric = type === "number";
    const displayValue = isNumeric && (value === 0 || value === "0" || value === undefined || value === null)
      ? ""
      : value;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isNumeric) {
        const el = e.currentTarget;
        // Sync select() first — fast users can type before any deferred
        // callback runs, so we must select before the next keystroke lands.
        try { el.select(); } catch {}
        // Safari sometimes places the caret after our sync call — re-run
        // on the next animation frame as a fallback.
        requestAnimationFrame(() => {
          try { el.select(); } catch {}
        });
      }
      onFocus?.(e);
    };
    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        onFocus={handleFocus}
        value={isNumeric ? (displayValue as any) : (value as any)}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }

